import type { ListTenantsResponse } from '$lib/fass-api';
import { Capacitor } from '@capacitor/core';
import { persisted } from 'svelte-local-storage-store';
import { get } from 'svelte/store';
import { loadTenants } from './tenants';
import { toast } from '@zerodevx/svelte-toast';

type ApiStore = {
	apiUrl: null | string;
	apiToken: null | string;
	tenant: ListTenantsResponse[0];
};

export const apiStore = persisted<ApiStore>('api-store', {
	apiUrl: null,
	apiToken: null,
	tenant: {
		subdomain: '',
		name: '',
		nameShort: '',
		logo: '',
		logoSm: '',
		iconColor: '',
		env: ''
	}
});

export function getApiUrlFromStore(): null | string {
	let apiUrl = "";
	const store = get(apiStore);

	if (store && store.apiUrl) {
		apiUrl = store.apiUrl;
	}

	if (!Capacitor.isNativePlatform()) {
		apiUrl = window.location.origin;
	}

	return apiUrl;
}

export function getTokenFromStore() {
	const store = get(apiStore);

	return store.apiToken ? { authorization: store.apiToken } : {};
}

type TenantDefinition = {
	subdomain: string;
	name: string;
	nameShort: string;
	logo: string;
	logoSm: string;
	iconColor: string;
};

const localTenantConfigs: Record<string, TenantDefinition> = {
	localhost: {
		subdomain: 'localhost',
		name: 'Localhost University',
		nameShort: 'MPU',
		logo: 'https://campus-community.b-cdn.net/mpu.edu/logo.png',
		logoSm: 'https://campus-community.b-cdn.net/mpu.edu/logo-sm.png',
		iconColor: '#000000'
	}
};

type InfraTierValues = 'production' | 'staging' | 'uat';

function extractTenantFromOrigin(origin: string): string {
	const parts = origin.split('.');
	return parts.length > 2 ? parts[0] : '';
}

export async function setTenant(mobileSubdomain?: string, infraTier: InfraTierValues = 'production') {
	const isMobileStaging = mobileSubdomain?.includes('.staging.campuscommunity.net');

	if (mobileSubdomain && isMobileStaging) {
		apiStore.set({
			apiToken: null,
			// TODO: this is a hacky way to get the api url from the mobile app
			apiUrl: `https://${mobileSubdomain}`,
			tenant: {
				name: 'MPU Staging',
				nameShort: 'MPU Staging',
				logo: 'https://campus-community.b-cdn.net/mpu.edu/logo.png',
				logoSm: 'https://campus-community.b-cdn.net/mpu.edu/logo-sm.png'
			}
		});

		return;
	}
	const tenantsList = await loadTenants(window.location.hostname);
	const infraTierPostFix = infraTier === 'production' ? '' : `.${infraTier}`;
	const origin = mobileSubdomain || window.location.hostname;
	const subdomain = mobileSubdomain || extractTenantFromOrigin(origin);

	const isLocalhost = origin.includes('localhost');
	// We can handle the case where the tenant is not found in the list of tenants, but
	// app will still work with the tenant from the origin.
	const isPreview = origin.includes('.staging.campuscommunity.net');
	const isSandbox = origin.includes('.sandbox.campuscommunity.net') || origin.includes('.sandbox.campuscommunity.app') || origin.includes('.onboarding.campuscommunity.net') || origin.includes('.uat.campuscommunity.app');



	const tenantsConfig = tenantsList.reduce(
		(acc, cur) => {
			acc[cur.subdomain] = cur;
			return acc;
		},
		{} as Record<string, TenantDefinition>
	);

	const finalConfig = { ...localTenantConfigs, ...tenantsConfig };

	const tenant = finalConfig[subdomain] || localTenantConfigs.default;
	// If the tenant is not found in the list of tenants, but the app is running in preview or sandbox mode:
	if (isPreview || isSandbox) {
		apiStore.set({
			apiToken: null,
			apiUrl: window.location.origin,
			tenant: tenant || {
				name: '',
				nameShort: '',
				logo: 'https://campuscommunity.io/wp-content/uploads/2024/09/cc_logo_large-768x190.webp',
				logoSm: 'https://campuscommunity.io/wp-content/uploads/2024/09/cc_logo_large-768x190.webp'
			}
		});
		return;
	}

	if (isLocalhost) {
		const tenant = localTenantConfigs.localhost;
		apiStore.set({
			apiToken: null,
			apiUrl: "http://localhost",
			tenant: {
				...tenant,
				logo: tenant?.logo,
				logoSm: tenant?.logoSm
			}
		});
		return;
	}

	// If the tenant is found in the list of tenants:
	if (tenant?.subdomain && !isLocalhost && !isPreview && !isSandbox) {
		apiStore.set({
			apiToken: null,
			apiUrl: `https://${tenant?.subdomain}${infraTierPostFix}.campuscommunity.app`,
			tenant: {
				...tenant,
				logo: tenant?.logo,
				logoSm: tenant?.logoSm
			}
		});
		return;
	}

}


export function clearSelectedTenant() {
	apiStore.set({
		apiUrl: null,
		apiToken: null,
		tenant: {
			logo: null,
			logoSm: null
		}
	});
}

/**
 * Initializes the API for web apps.
 * Sets the tenant and sets a flag to indicate that the tenant has been set on desktop.
 */
export async function webInitializeAPI(): Promise<boolean> {
	await setTenant(undefined);
	return true;
}

export function resetApiStore() {
	apiStore.set({
		apiUrl: null,
		apiToken: null,
		tenant: {
			logo: null,
			logoSm: null
		}
	});
}

export function setApiToken(token: string) {
	const store = get(apiStore);
	apiStore.set({
		...store,
		apiToken: token
	});
}

interface BackoffFetchOptions extends RequestInit {
	retries?: number;
	initialDelay?: number;
	onError?: (error: Error) => void;
	onRetry?: (retriesLeft: number) => void;
	validateResponse?: (response: Response) => Promise<boolean> | boolean;
}

const DEFAULT_OPTIONS = {
	retries: 3,
	initialDelay: 1000,
	validateResponse: (response: Response) => response.ok
} as const;

const backoffFetch = async (
	url: string,
	options: BackoffFetchOptions = {}
): Promise<Response> => {
	const {
		retries = DEFAULT_OPTIONS.retries,
		initialDelay = DEFAULT_OPTIONS.initialDelay,
		onError,
		onRetry,
		validateResponse = DEFAULT_OPTIONS.validateResponse,
		...fetchOptions
	} = options;

	let currentRetry = retries;
	let currentDelay = initialDelay;

	while (currentRetry >= 0) {
		try {
			const response = await fetch(url, fetchOptions);
			
			const isValid = await validateResponse(response);
			if (isValid) {
				return response;
			}
			
			throw new Error(`Request failed with status ${response.status}`);
		} catch (error) {
			if (currentRetry === 0) {
				onError?.(error as Error);
				throw error;
			}

			await new Promise(resolve => setTimeout(resolve, currentDelay));
			onRetry?.(currentRetry);
			
			currentRetry--;
			currentDelay *= 2;
		}
	}

	// This should never be reached due to the throw in the catch block
	throw new Error('Unexpected end of backoffFetch');
};

// --------------------------------------------------------------------------------------------
// Adding this to prod as front-running change towards centralized tenant API
// - For web we will load the config from https://$origin/api/config
// --------------------------------------------------------------------------------------------

export async function loadWebConfig() {
	const currentOrigin = window.location.hostname;
	const protocol = currentOrigin === "localhost" ? window.location.protocol : "https:"
	const configPath = `${protocol}//${currentOrigin}/api/config`

	try {
		const config = await backoffFetch(configPath, {
			retries: 3,
			initialDelay: 1000,
			onError: () => {
				toast.push('Our app is having trouble loading right now.');
			}
		}).then(r => r.json())
	
		// compare origin with config.subdomain
		if (currentOrigin !== config.subdomain) {
			console.error(`Subdomain mismatch: ${currentOrigin} !== ${config.subdomain}`);
		}
	
		apiStore.set({
			apiToken: null,
			apiUrl: `${protocol}//${currentOrigin}`,
			tenant: {
				name: config.name,
				nameShort: config.nameShort,
				logo: config.logoUrl,
				// If there is no logoSmallUrl, use the logoUrl for mobile as a fallback
				logoSm: config.logoSmallUrl ? config.logoSmallUrl : config.logoUrl
			}
		});
	
	} catch (error) {
		console.error('Error loading web config', error);
		return false;
	}
	return true;
}